import React, { useRef } from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import { ShiftPositionType } from "@/types/db/shift_positions";
import { calculatePositionStats } from "./utils";
import { PositionTooltipContent } from "./PositionTooltipContent";

type PositionPillProps = {
  position: ShiftPositionType;
  index: number;
};

export function PositionPill({ position, index }: PositionPillProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();

  const stats = calculatePositionStats(position);
  const filledCount = stats.accepted + stats.invited + stats.queued + stats.complete;
  const isFullyAccepted = stats.accepted === stats.total && stats.total > 0;
  const ref = useRef<View>(null);

  const handlePointerEnter = () => {
    ref.current?.measureInWindow((x, y, width) => toolTip.setLocation({ x: x + width + 2, y: y }));
    toolTip.setToolTipContent(<PositionTooltipContent position={position} stats={stats} />);
    toolTip.setVisible(true);
  };

  const handlePointerLeave = () => {
    toolTip.setVisible(false);
  };

  return (
    <View
      key={`${position.id} - ${index}`}
      ref={ref}
      style={[
        styles.pill,
        {
          backgroundColor: colors.backgroundSecondary,
          borderColor: isFullyAccepted ? colors.emerald.border : colors.border,
        },
      ]}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <ThemedText style={styles.pillText}>
        {(position.position?.name || "POS").substring(0, 3).toUpperCase()}
      </ThemedText>
      <ThemedText style={[styles.pillCount, { color: isFullyAccepted ? colors.emerald.text : colors.placeholder }]}>
        {filledCount}/{position.quantity || 0}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: StyleSheet.hairlineWidth,
  },
  pillText: {
    fontSize: 9,
    fontWeight: "600",
    opacity: 0.7,
  },
  pillCount: {
    fontSize: 9,
    fontWeight: "700",
  },
});
